const UTF8_BOM = '﻿';
const UTF8_BOM_BYTES = [0xef, 0xbb, 0xbf];

// Checks whether a blob already starts with a UTF-8 BOM, so we don't
// double-prepend one if the backend ever starts sending it itself.
function hasUtf8Bom(blob: Blob): Promise<boolean> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const header = new Uint8Array(reader.result as ArrayBuffer);
      resolve(UTF8_BOM_BYTES.every((byte, index) => header[index] === byte));
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(blob.slice(0, 3));
  });
}

/**
 * Downloads a file from a given URL.
 *
 * @param {function} baseQuery - A function that performs the query to retrieve the file.
 *                               It should return a promise that resolves to an object containing the file data as a blob.
 * @param {string} url - The URL of the file to be downloaded.
 * @param {string} [fileName] - An optional name for the downloaded file.
 *                              If not provided, the file will be opened in a new tab instead of being downloaded.
 * @param {string} [method] - The HTTP method to use. Defaults to 'GET'.
 * @param {Record<string, string | undefined>} [params] - Optional query parameters. Undefined values are omitted.
 */
export async function fetchAndDownloadFile(
  baseQuery: (arg: unknown) => unknown | Promise<unknown>,
  url: string,
  fileName?: string,
  method?: string,
  params?: Record<string, string | undefined>
) {
  const filteredParams = params
    ? Object.fromEntries(
        Object.entries(params).filter(([, v]) => v !== undefined)
      )
    : undefined;

  const result = (await baseQuery({
    url: url,
    method: method ?? 'GET',
    params: filteredParams,
    responseHandler: (response: { blob: () => unknown }) => response.blob(),
  })) as { data: Blob };
  const blob = result.data;

  // CSV responses come back as valid UTF-8 but usually without a BOM, so
  // Excel falls back to Windows-1252 and garbles accented characters.
  // Prepend the BOM for CSV downloads only, matching the fix in
  // exportCsv.ts — this assumes the backend always returns valid UTF-8
  // bytes for `.csv`-suffixed exports; if a future `.csv` caller returns
  // a different encoding, revisit this check rather than reusing it as-is.
  // Skip it if the backend already added its own BOM, to avoid doubling it.
  const isCsv = fileName?.endsWith('.csv') ?? false;
  const downloadBlob =
    isCsv && !(await hasUtf8Bom(blob))
      ? new Blob([UTF8_BOM, blob], { type: 'text/csv;charset=utf-8' })
      : blob;

  // Create a temporary URL for the blob
  const fileUrl = URL.createObjectURL(downloadBlob);

  if (fileName) {
    // If a file name is provided, create a temporary anchor element to trigger the download with the file name
    const a = document.createElement('a');
    a.href = fileUrl;
    a.download = fileName ?? 'file';

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // Revoke the temporary URL to free up resources
    URL.revokeObjectURL(fileUrl);
  } else {
    window.open(fileUrl);
  }

  // Return an empty data object to do not save data in the redux state
  return { data: undefined };
}
